import React, { useState, useEffect, useMemo, useCallback } from "react";
import { authFetch, API_BASE } from "../api";
import "./UsagePage.css";

/**
 * UsagePage — admin dashboard for AI token usage + cost.
 *
 * Pulls aggregations from /api/ai-usage/summary and /totals. No raw row
 * access; this page is shaped for "where is the money going" reports,
 * not per-iteration debugging (that's the Logs page).
 *
 * Visible to admin role only — gated upstream in App.js / Sidebar.
 *
 * Layout: time-range picker at top → totals strip → daily sparkline →
 * three stacked tables (per-user, per-app, per-operation). Each table
 * shows top 20 buckets by cost. For deeper analysis (CSV export,
 * monthly comparisons, forecasting) the underlying endpoints are
 * scriptable today; UI for those is a follow-up if usage grows.
 */
export default function UsagePage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState({ totals: null, byUser: [], byApp: [], byOperation: [], byDay: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const since = new Date(Date.now() - days * 86_400_000).toISOString();
      const qs = `since=${encodeURIComponent(since)}`;
      const [totalsRes, userRes, appRes, opRes, dayRes] = await Promise.all([
        authFetch(`${API_BASE}/ai-usage/totals?${qs}`),
        authFetch(`${API_BASE}/ai-usage/summary?groupBy=user&${qs}&limit=20`),
        authFetch(`${API_BASE}/ai-usage/summary?groupBy=app&${qs}&limit=20`),
        authFetch(`${API_BASE}/ai-usage/summary?groupBy=operation&${qs}&limit=20`),
        authFetch(`${API_BASE}/ai-usage/summary?groupBy=day&${qs}&limit=120`),
      ]);
      const [totals, user, app, op, day] = await Promise.all([
        totalsRes.json(), userRes.json(), appRes.json(), opRes.json(), dayRes.json(),
      ]);
      // Sort daily ascending so the sparkline reads left-to-right.
      const byDay = (day.rows || []).slice().sort((a, b) => a.bucket.localeCompare(b.bucket));
      setData({
        totals,
        byUser: user.rows || [],
        byApp: app.rows || [],
        byOperation: op.rows || [],
        byDay,
      });
    } catch (err) {
      setError(err.message || "Failed to load usage data");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="usage-page">
      <div className="page-header">
        <h2>AI Usage</h2>
        <div className="page-header-right">
          <div className="usage-range">
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                className={`usage-range-btn ${days === d ? "active" : ""}`}
                onClick={() => setDays(d)}
              >
                {d === 365 ? "1y" : `${d}d`}
              </button>
            ))}
          </div>
          <button className="usage-refresh" onClick={fetchData} title="Refresh" disabled={loading}>
            ↻
          </button>
        </div>
      </div>

      {error && <div className="usage-error">{error}</div>}

      {/* Top totals strip */}
      {data.totals && (
        <div className="usage-totals">
          <Stat label="Total cost" value={data.totals.costUsd} primary />
          <Stat label="API calls" value={fmtInt(data.totals.calls)} />
          <Stat label="Input tokens" value={fmtInt(data.totals.inputTokens)} />
          <Stat label="Output tokens" value={fmtInt(data.totals.outputTokens)} />
          <Stat
            label="Cache hit ratio"
            value={cacheHitRatio(data.totals)}
            hint={`${fmtInt(data.totals.cacheReadTokens)} of ${fmtInt(data.totals.cacheReadTokens + data.totals.inputTokens)} input tokens served from cache`}
          />
        </div>
      )}

      {/* Daily sparkline (bar chart) */}
      {data.byDay.length > 0 && (
        <div className="usage-section">
          <div className="usage-section-title">Daily cost</div>
          <DailyBars rows={data.byDay} />
        </div>
      )}

      {/* Per-bucket tables */}
      <div className="usage-tables">
        <BucketTable title="By user" rows={data.byUser} bucketLabel="User" />
        <BucketTable title="By app" rows={data.byApp} bucketLabel="App" />
        <BucketTable title="By operation" rows={data.byOperation} bucketLabel="Operation" />
      </div>

      {loading && data.byUser.length === 0 && (
        <div className="usage-loading">Loading…</div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

function Stat({ label, value, hint, primary }) {
  return (
    <div className={`usage-stat ${primary ? "primary" : ""}`} title={hint || ""}>
      <div className="usage-stat-label">{label}</div>
      <div className="usage-stat-value">{value}</div>
    </div>
  );
}

function BucketTable({ title, rows, bucketLabel }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="usage-table">
        <div className="usage-table-title">{title}</div>
        <div className="usage-empty">No data in this window.</div>
      </div>
    );
  }
  // Max cost in the visible rows — used to render the inline bar.
  const max = rows.reduce((m, r) => Math.max(m, r.costUsdMicro || 0), 1);
  return (
    <div className="usage-table">
      <div className="usage-table-title">{title}</div>
      <div className="usage-table-rows">
        <div className="usage-table-head">
          <span className="usage-col-bucket">{bucketLabel}</span>
          <span className="usage-col-num">Calls</span>
          <span className="usage-col-num">Input</span>
          <span className="usage-col-num">Output</span>
          <span className="usage-col-cost">Cost</span>
        </div>
        {rows.map((r) => {
          const pct = ((r.costUsdMicro || 0) / max) * 100;
          return (
            <div key={r.bucket || "(none)"} className="usage-table-row">
              <span className="usage-col-bucket" title={r.bucket}>
                {r.bucket || <em className="usage-null">(none)</em>}
              </span>
              <span className="usage-col-num" data-label="Calls">{fmtInt(r.calls)}</span>
              <span className="usage-col-num" data-label="Input">{fmtInt(r.inputTokens)}</span>
              <span className="usage-col-num" data-label="Output">{fmtInt(r.outputTokens)}</span>
              <span className="usage-col-cost">
                <span className="usage-bar" style={{ width: `${pct}%` }} />
                <span className="usage-bar-label">{r.costUsd}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DailyBars({ rows }) {
  // Pad missing days so the sparkline is visually continuous.
  const filled = useMemo(() => fillMissingDays(rows), [rows]);
  const max = filled.reduce((m, r) => Math.max(m, r.costUsdMicro || 0), 1);
  return (
    <div className="usage-bars">
      {filled.map((r) => {
        const h = ((r.costUsdMicro || 0) / max) * 100;
        return (
          <div
            key={r.bucket}
            className="usage-bar-col"
            title={`${r.bucket}: ${r.costUsd} (${fmtInt(r.calls)} calls)`}
          >
            <div className="usage-bar-fill" style={{ height: `${h}%` }} />
            <div className="usage-bar-tick">{r.bucket.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────

function fmtInt(n) {
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US");
}

function cacheHitRatio(totals) {
  const cached = totals.cacheReadTokens || 0;
  const fresh = totals.inputTokens || 0;
  const total = cached + fresh;
  if (total === 0) return "—";
  return `${Math.round((cached / total) * 100)}%`;
}

/**
 * Fill in zero-cost days between min and max bucket dates so the bar
 * chart doesn't visually compress missing days. The summary endpoint
 * naturally only returns rows that exist; for a chart we want a row
 * per day in the range.
 */
function fillMissingDays(rows) {
  if (rows.length === 0) return [];
  const byBucket = new Map(rows.map((r) => [r.bucket, r]));
  const start = rows[0].bucket;
  const end = rows[rows.length - 1].bucket;
  const out = [];
  const cursor = new Date(start + "T00:00:00Z");
  const stop = new Date(end + "T00:00:00Z");
  while (cursor <= stop) {
    const key = cursor.toISOString().slice(0, 10);
    out.push(byBucket.get(key) || { bucket: key, calls: 0, inputTokens: 0, outputTokens: 0, costUsdMicro: 0, costUsd: "$0" });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}
